// GMap.jsx
/* eslint-disable react/prop-types */
/* eslint-disable react/react-in-jsx-scope */
import { CircleF, GoogleMap, InfoWindowF, MarkerF, PolylineF, useLoadScript } from '@react-google-maps/api';
import '../../App.css';
import React, { useEffect, useState, useCallback, useRef} from 'react';
import { useAuth } from '../Context';
import WBicon from '../../images/wb.png';
import KMPicon from '../../images/kmp.png';
import TLPicon from '../../images/tlp.png';
import TPicon from '../../images/tp.png';
import CHicon from '../../images/ch.png';
import eventsicons from '../eventstypes';
import pids from '../../images/Refinery1.png';
import defaultimg from '../../images/default1.png';
import LWmarker from '../../images/LWmarker.png';
import QRTmarker from '../../images/INSPQRT.png';
import vesselcpa from '../../images/markerimages/cpapointmarker.png';

import LWIcon from '../../images/man.png';
import QRTIcon from '../../images/QRTimg.png';

// import point from '../../images/red-pin.png';
import StartIcon from '../../images/start.png';
import EndIcon from '../../images/stop.png';
import smartTLPicon from '../../images/tlpred.png';
import wellicon from '../../images/oil-well.png';

import car from '../../images/car32.png';
import arabiclan from '../../arabic.json';
import englan from '../../english.json';
import frenchlan from '../../french.json';
import germanlan from '../../german.json';
import hindilan from '../../hindi.json';
import malayalamlan from '../../malayalam.json';
import { datetimeCovertertolocal } from '../datetime-converter';

const dashboardtype = process.env.REACT_APP_IDENTIFIER;

const langid = localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) && localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) ? parseInt(localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`)) : 2;
const langdata = langid && langid ==1 ? arabiclan : langid ==2 ? englan : langid ==3 ? frenchlan : langid ==4 ? germanlan : langid ==5 ? hindilan : langid == 6 ? malayalamlan : englan;

// Needed (same as SeaMap.jsx) to gate the "Algorithm" row in the
// per-point hover title to superadmin only — see buildDetectionPointTitle
// further down.
const permission = localStorage.getItem(`${dashboardtype.toUpperCase()}_PERMISSION`);

// ============================================================
// PER-POINT VALUE HELPER (ported 1:1 from SeaMap.jsx)
//
// Several detection fields (speed, cpa distance, start-time) can come
// back from the API either as a single scalar shared by the whole
// trajectory, OR as an array with one entry per point. GMap previously
// always read the trajectory-level field directly as a scalar (e.g.
// `trajectory.sourceSpeedms`) in the starting-point marker's title.
// When that field actually held a per-point ARRAY, string-interpolating
// it straight into a template literal joins every entry with commas —
// that's what produced the duplicated/garbled "3.05,3.05 m/s" value.
// It's also why every point OTHER than the start point showed no speed
// at all: nothing ever indexed into that array for them.
//
// pickValueForIndex fixes both: if the value is an array, it picks the
// entry at THIS point's index; if it's a plain scalar, it just returns
// it as-is (so it's still safe for any legacy non-array field).
// ============================================================
const pickValueForIndex = (data, index) => {
	if (Array.isArray(data)) {
		return index >= 0 && index < data.length
			? data[index]
			: null;
	}
	return data ?? null;
};

// import { route } from "./data";

// ============================================================
// Normalizes a `center` prop into a Google Maps {lat, lng} literal.
// Callers pass this in two different shapes across the app:
//   - an [lat, lng] pair (e.g. RealtimeDetectionDetails.jsx)
//   - a {lat, lng} object (e.g. the existing linewalkerData block below)
// This accepts either so a caller doesn't need to know which one GMap
// expects internally.
// ============================================================
const resolveCenterProp = (value) => {
	if (!value) {
		return null;
	}

	if (Array.isArray(value)) {
		const [lat, lng] = value;
		if (lat === undefined || lat === null || lng === undefined || lng === null) {
			return null;
		}
		return { lat: parseFloat(lat), lng: parseFloat(lng) };
	}

	if (value.lat !== undefined && value.lng !== undefined) {
		return { lat: parseFloat(value.lat), lng: parseFloat(value.lng) };
	}

	return null;
};

// ============================================================
// REFERENCE-COORDINATE / FIBRE-DETECTION MARKER ICONS
//
// Ported 1:1 (visually) from SeaMap.jsx's inline divIcon SVGs:
//   - coordinateIcon        -> a violet map pin, used for plotted
//                              "reference coordinate" (interrogator) points.
//   - peakCoordinateIcon    -> a small rotated orange square, used for a
//                              detection trajectory's peakCoordinates
//                              ("Fibre Detection") point.
// Google Maps markers don't support raw HTML divIcons, so these are
// built as inline SVG data-URIs instead — same shapes/colors as SeaMap.
// ============================================================
const COORDINATE_PIN_COLOR = '#eedb84';

const coordinatePinIconUrl =
	'data:image/svg+xml;charset=UTF-8,' +
	encodeURIComponent(`
		<svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z"
				fill="${COORDINATE_PIN_COLOR}"
				stroke="#000000"
				stroke-width="2"
			/>
			<circle cx="15" cy="15" r="6" fill="#000000" />
		</svg>
	`);

const peakCoordinateIconUrl =
	'data:image/svg+xml;charset=UTF-8,' +
	encodeURIComponent(`
		<svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
			<rect x="4" y="4" width="10" height="10" fill="#f75a00" stroke="#000000" stroke-width="1" transform="rotate(110 9 9)" />
		</svg>
	`);

// Dashed-line style for Google Maps polylines (Google Maps has no native
// "dashArray" like Leaflet — dashes are simulated with a repeating line
// symbol icon along an invisible (strokeOpacity: 0) base line). Used for
// both "Ship-Fibre Link" and "Coordinate-Fibre Link", same as the dashed
// magenta/algorithm-colored lines in SeaMap.jsx.
const dashedLineIcons = [{
	icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, scale: 3 },
	offset: '0',
	repeat: '10px'
}];

// ============================================================
// STABLE MODULE-LEVEL CONSTANTS
//
// These MUST be declared once, outside the component, and never
// recreated on every render:
//
//   - GOOGLE_MAPS_LIBRARIES: passed to useLoadScript's `libraries`
//     option. If this were a fresh [] literal created inside the
//     component body, @react-google-maps/api sees a "new" array
//     reference on every re-render and force-reloads the entire
//     Google Maps script — which can tear down an already-mounted
//     map (this was the root cause of the map flickering / going
//     blank on unrelated re-renders, e.g. toggling a checkbox or an
//     AIS poll tick).
//
//   - FALLBACK_CENTER: used only when no real center could be
//     computed yet (e.g. the Map view is opened with just
//     `datadetection`/`coordinatesData`/`showAISVessels`, no
//     `regionId`, so `mapdata` is still empty and `center` stays
//     `[]`). Passing `defaultCenter={null}` into <GoogleMap> can
//     leave it rendering nothing at all (a blank grey box) — this
//     guarantees there's always a valid center to mount with.
// ============================================================
const GOOGLE_MAPS_LIBRARIES = [];

// TODO: adjust to whatever coordinate makes sense as a default for
// your pipeline/region (e.g. the first region's midpoint). This is
// only ever shown for a split second before real data centers the
// map, or if no data loads at all.
const FALLBACK_CENTER = { lat: 20.5937, lng: 78.9629 };

const OPENWATERS_VESSELS_URL = 'https://ais.openwaters.io/v1/vessels';

// AIS vessels kept in their OWN component so a 15s poll only re-renders
// this tiny piece — not the whole GMap tree (pipelines/KMP/CH/wells/
// detections), which is what was causing the "map reload" flicker.
const AisVesselsLayer = ({ map, enabled }) => {
	const [vessels, setVessels] = useState([]);

	const fetchAbortRef = useRef(null);

	const fetchVessels = useCallback(() => {
		if (!map || !enabled) return;

		const bounds = map.getBounds();
		if (!bounds) return;

		const southWest = bounds.getSouthWest();
		const northEast = bounds.getNorthEast();

		const bbox = [
			southWest.lat(),
			southWest.lng(),
			northEast.lat(),
			northEast.lng()
		].join(',');

		// Cancel any in-flight request before starting a new one — without
		// this, a slow previous request can still resolve LATER and
		// overwrite fresher data with stale results, and multiple overlapping
		// requests compete for bandwidth, which is part of why things can
		// feel slower than they should.
		if (fetchAbortRef.current) {
			fetchAbortRef.current.abort();
		}
		const controller = new AbortController();
		fetchAbortRef.current = controller;

		// Fail fast instead of hanging indefinitely on a slow/unresponsive
		// AIS endpoint — 4s here (tune as needed) instead of waiting on
		// whatever the browser's default TCP timeout would be.
		const timeoutId = setTimeout(() => controller.abort(), 4000);

		fetch(`${OPENWATERS_VESSELS_URL}?bbox=${encodeURIComponent(bbox)}`, {
			signal: controller.signal
		})
			.then((response) => {
				clearTimeout(timeoutId);
				if (!response.ok) throw new Error(`AIS request failed: ${response.status}`);
				return response.json();
			})
			.then((geojson) => {
				const features = Array.isArray(geojson?.features) ? geojson.features : [];

				const parsed = features
					.filter((feature) => {
						const kind = feature?.properties?.kind;
						return !kind || kind === 'vessel';
					})
					.map((feature) => {
						const coordinates = feature?.geometry?.coordinates || [];
						const properties = feature?.properties || {};
						return {
							mmsi: properties.mmsi ?? feature.id ?? null,
							name: properties.name || null,
							cog: Number.isFinite(Number(properties.cog)) ? Number(properties.cog) : null,
							sog: Number.isFinite(Number(properties.sog)) ? Number(properties.sog) : null,
							heading: Number.isFinite(Number(properties.heading)) ? Number(properties.heading) : null,
							seen: properties.seen ?? null,
							source: properties.source ?? null,
							lat: Number(coordinates[1]),
							lng: Number(coordinates[0]),
						};
					})
					.filter((vessel) => Number.isFinite(vessel.lat) && Number.isFinite(vessel.lng))
					.slice(0, 200);

				setVessels(parsed);
			})
			.catch((error) => {
				clearTimeout(timeoutId);
				if (error.name !== 'AbortError') {
					console.error('OpenWaters AIS error:', error);
				}
			});
	}, [map, enabled]);

	useEffect(() => {
		if (!map || !enabled) {
			setVessels([]);
			return undefined;
		}

		// 'idle' fires the moment the map has finished loading tiles and its
		// bounds are guaranteed to be valid — this replaces the old blind
		// 1000ms setTimeout guess, so the very first AIS fetch fires as soon
		// as it actually CAN (often much sooner than 1s), instead of always
		// waiting a full second regardless of how ready the map already is.
		const idleListener = map.addListener('idle', fetchVessels);

		// Still fetch immediately in case 'idle' has already fired before
		// this listener was attached (e.g. map was already idle).
		fetchVessels();

		const interval = setInterval(fetchVessels, 15000);

		return () => {
			if (idleListener) idleListener.remove();
			clearInterval(interval);
		};
	}, [map, enabled, fetchVessels]);

	// Guard against rendering before the Google Maps script/global is
	// actually ready (e.g. a stray render right around script load) —
	// window.google.maps.SymbolPath would throw otherwise and, with no
	// error boundary around it, can blank the entire map tree.
	if (!enabled || typeof window === 'undefined' || !window.google || !window.google.maps) {
		return null;
	}

	return (
		<>
			{vessels.map((vessel) => {
				const rotation = Number.isFinite(vessel.heading) ? vessel.heading : (vessel.cog || 0);

				const newTime = datetimeCovertertolocal(vessel.seen);

				const vesselTitle =
	`MMSI: ${vessel.mmsi ?? 'No Data'}\n` +
	`${langdata && langdata.Latitude ? langdata.Latitude : 'Latitude'}: ${vessel.lat ?? 'No Data'}\n` +
	`${langdata && langdata.Longitude ? langdata.Longitude : 'Longitude'}: ${vessel.lng ?? 'No Data'}\n` +
	`${langdata && langdata.Speed ? langdata.Speed : 'Speed'}: ${vessel.sog ?? 'No Data'} kn\n` +
	`${langdata && langdata.Course ? langdata.Course : 'Course'}: ${vessel.cog ?? 'No Data'}°` +
	`${vessel.seen ? `\n${langdata && langdata.dateTime ? langdata.dateTime : 'Date Time'}: ${newTime.localdate} ${newTime.localtime}` : ''}` +
	`${vessel.source ? `\n${langdata && langdata.Source ? langdata.Source : 'Source'}: ${vessel.source}` : ''}`;
				return (
					<MarkerF
						key={`ais-${vessel.mmsi}`}
						position={{ lat: vessel.lat, lng: vessel.lng }}
						icon={{
							path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
							scale: 5,
							fillColor: '#fccb2c',
							fillOpacity: 1,
							strokeColor: '#000000',
							strokeWeight: 1,
							rotation
						}}
						title={vesselTitle}
						zIndex={1000}
					/>
				);
			})}
		</>
	);
};

const GMap = (props) => {
	// const [polygonPath, setPolygonPath] = useState();
	const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

	const { isLoaded } = useLoadScript({
		googleMapsApiKey: GOOGLE_MAP_API_KEY,
		libraries: GOOGLE_MAPS_LIBRARIES
	});

	const {onClick } = props;

	// Detection trajectories' Points / Poly Lines visibility — controlled by
	// the "POINTS" / "POLY LINES" checkboxes rendered next to PLAY in
	// Map.jsx. Points defaults ON, Poly Lines defaults OFF; any caller not
	// passing these props gets the same safe defaults.
	const showDetectionPoints = props.changeDetectionPoints !== false;
	const showDetectionPolyline = props.changeDetectionPolyline === true;

	// Fibre Detection (peak-coordinate marker) / Ship-Fibre Link (dashed line
	// from every plotted ship point to that peak coordinate) — controlled by
	// the "FIBRE DETECTION" / "SHIP-FIBRE LINK" checkboxes in Map.jsx.
	// Fibre Detection defaults ON, Ship-Fibre Link defaults OFF — same
	// defaults as SeaMap.jsx's equivalent Overlays.
	const showFibreDetection = props.changeFibreDetection !== false;
	const showShipFibreLink = props.changeShipFibreLink === true;

	// Reference Coordinates (plotted interrogator points) / Coordinate-Fibre
	// Link (dashed line from a coordinate to its fibre point) — controlled by
	// the "REFERENCE COORDINATES" / "COORDINATE-FIBRE LINK" checkboxes in
	// Map.jsx. Reference Coordinates defaults ON, Coordinate-Fibre Link
	// defaults OFF — same defaults as SeaMap.jsx.
	const showReferenceCoordinate = props.changeReferenceCoordinates !== false;
	const showCoordinateFibreLine = props.changeCoordinateFibreLink === true;
	const showAISVessels = props.changeAISVessels === true;

	const coordinatesData = props.coordinatesData || [];
	const onDeleteCoordinate = props.onDeleteCoordinate;

	// Which plotted reference-coordinate marker currently has its InfoWindow
	// open, and delete-confirmation state for it — mirrors the click-to-open
	// Popup + inline "Delete" confirm flow in SeaMap.jsx's
	// CoordinateMarker/CoordinateDeleteActions.
	const [activeCoordinateId, setActiveCoordinateId] = useState(null);
	const [confirmDeleteCoordinateId, setConfirmDeleteCoordinateId] = useState(null);
	const [deletingCoordinateId, setDeletingCoordinateId] = useState(null);

	const {handleGetMapdata, mapdata, optionalpipedata, handleOptionalPipelinedata,wellinfo, getWellInfo,} = useAuth();
	const dashboardtype = process.env.REACT_APP_IDENTIFIER;
	const authtoken = localStorage.getItem(`${dashboardtype.toUpperCase()}_ACCESS_TOKEN`);
	const regionId = props.regionId;
	const startCh = props.startCh;
	const endCh = props.endCh;
	const pipelineId = localStorage.getItem(`${dashboardtype.toUpperCase()}_pipelineId`) ? localStorage.getItem(`${dashboardtype.toUpperCase()}_pipelineId`) : null;
	const allPidsData = JSON.parse(localStorage.getItem(`${dashboardtype.toUpperCase()}_PIDS`)) || [];
	const oneviewType = process.env.REACT_APP_ONEVIEW_TYPE ? process.env.REACT_APP_ONEVIEW_TYPE : 'pipeline';
	const langid = localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) && localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) ? parseInt(localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`)) : 2;
	const langdata = langid && langid ==1 ? arabiclan : langid ==2 ? englan : langid ==3 ? frenchlan : langid ==4 ? germanlan : langid ==5 ? hindilan : langid == 6 ? malayalamlan : englan;
	const seadataprops = props.datadetection;

	// True whenever there's at least one detection record to animate. Relaxed from
	// an exact "length === 1" check — PLAY now animates EVERY loaded detection (see the
	// multi-trajectory effect below), not just the first, so requiring exactly one entry
	// was an unnecessarily strict condition that could leave PLAY permanently disabled if
	// the array shape varied even slightly.
	const hasDetection = Array.isArray(seadataprops) && seadataprops.length > 0;

	// Stable key built from EVERY currently loaded detection's id (not just the first),
	// used as an effect dependency so the PLAY animation restarts whenever the *set* of
	// loaded detections changes (navigating to a different single detection, or
	// RealtimeDetection.jsx's map view loading a different batch of detections) but
	// doesn't restart on every unrelated re-render (props.datadetection is rebuilt as a
	// new array reference on every render of the caller).
	const detectionIdsKey = hasDetection ? seadataprops.map((d) => d && d.id).join(',') : '';



	const [stateCenter, setCenter] = useState();
	useEffect(() => {
		handleGetMapdata(authtoken,regionId,startCh,endCh);
		handleOptionalPipelinedata(authtoken,pipelineId,regionId,startCh,endCh);
		if(oneviewType=='well'){
			getWellInfo(authtoken, null);
		}
	},[regionId, startCh, endCh]);


	const [playinspectionLine, setInspectionLine] = useState([]);

	useEffect(() => {
		// Legacy walking/patrol-line replay (linewalker / patch / vulnerable inspection
		// data). Only runs when there's no realtime-detection data loaded — detections use
		// the dedicated multi-trajectory animation below instead, so every plotted
		// detection (not just the first) gets its own moving marker + growing line.
		if (hasDetection) {
			setInspectionLine([]);
			return;
		}

		const sourceLine = inspectionline;

		if (!sourceLine.length || !props.changePlayinspectionLine) return;

		setInspectionLine([]);
		let index = 0;

		const interval = setInterval(() => {
			if (index >= sourceLine.length) {
				clearInterval(interval);
				return;
			}

			setInspectionLine(prev => {
				const point = sourceLine[index];
				if (!point) return prev;
				return [...prev, point];
			});
			index++;
		}, 500);
		return () => clearInterval(interval);
	}, [props.changePlayinspectionLine, hasDetection, detectionIdsKey]);

	// ============================================================
	// PLAY ANIMATION FOR DETECTION TRAJECTORIES
	// Instead of rebuilding a brand-new set of {lat,lng} points in a
	// setInterval (which meant the animated green line was a SEPARATE
	// array from the static red line, and could end up subtly offset
	// from it), this now just tracks HOW MANY points have "played" so
	// far, as a single number. The green line is then rendered as a
	// slice of the *exact same* `trajectoryPath(trajectory)` array
	// used to draw the static line below — so it is guaranteed to sit
	// exactly on top of the red line, point for point, and by the time
	// playedCount reaches the full length it exactly covers the whole
	// red line (same coordinates, not a re-derived approximation).
	// ============================================================
	const [playedCount, setPlayedCount] = useState(0);

	useEffect(() => {
		if (!hasDetection || !props.changePlayinspectionLine || detectionTrajectories.length === 0) {
			setPlayedCount(0);
			return;
		}

		setPlayedCount(0);
		let index = 0;
		const maxLength = Math.max(...detectionTrajectories.map((trajectory) => (trajectory.points ? trajectory.points.length : 0)));

		const interval = setInterval(() => {
			index += 1;
			if (index > maxLength) {
				clearInterval(interval);
				return;
			}
			setPlayedCount(index);
		}, 500);

		return () => clearInterval(interval);
	}, [props.changePlayinspectionLine, detectionIdsKey]);

  
	let center = stateCenter ? [stateCenter] : [];
	let zoom = 14;
	// let pipeline = [];
	// let pipelineBorder = [];
	// let odmeter = [];
	let markers = [];
	let KMP = [];
	let WB = [];
	let Turn = [];
	let TLP = [];
	// let optionalpipeline = [];
	let event_markers = [];
	let event_details_marker = [];
	let alarm_markers = [];
	let alarm_details_marker = [];
	let allPidsDatas = [];
	let pig_data = [];
	let linewalker_markers = [];
	let inspectionline = [];
	let startAllocation = [];
	let endAllocation = [];
	let inspectionPoints = [];
	let tlp_details_marker = [];
	let pigPidsData = [];

	let wellMarkers=[];

	let sameinspectionline = [];
	let oppositeinspectionline = [];

	const tempMapArray = {};
	const teampOptionalMapArray = {};

	mapdata && mapdata.map((item) => {
		const fieldValue = item['regioninfoId'];
		if(!tempMapArray[fieldValue]) {
			tempMapArray[fieldValue] = [];
		}
		tempMapArray[fieldValue].push(item);
	});

	optionalpipedata && optionalpipedata.map((item) => {
		const fieldValue = item['regioninfoId'];
		if(!teampOptionalMapArray[fieldValue]) {
			teampOptionalMapArray[fieldValue] = [];
		}
		teampOptionalMapArray[fieldValue].push(item);
	});

	mapdata &&  mapdata.map((item,index)=>{
		if(index==0){
			center.push({lat : item.latitude,lng : item.longitude});
		}

		if(item.rouMarker=='CH' && props.changeCH === true){
			markers.push(
				{
					geocode:[item.latitude,item.longitude],
					landmarkDescription: item?.landmarkDescription,
					pipelineChainage: item?.pipelineChainage,
					ODMeter: item?.ODMeter !== null && item?.ODMeter !== undefined ? item.ODMeter : null,
				},
			);
		}
		else if(item.rouMarker=='WB' && props.changeWB === true){
			WB.push(				{
				geocode:[item.latitude,item.longitude],
				landmarkDescription: item?.landmarkDescription,
				pipelineChainage: item?.pipelineChainage
				
			},);
		}

		else if(item.rouMarker=='TLP' && props.changeTLP === true){
			if(props.TLPData=='TLPData'){
				TLP.push(				{
					geocode:[item.latitude,item.longitude],
					landmarkDescription: item?.landmarkDescription,
					pipelineChainage: item?.pipelineChainage,
					smartTLP : item.tlpId != 0 ? true : false,
				},);
				
			}else{
				TLP.push(
					{
						geocode:[item.latitude,item.longitude],
						landmarkDescription: item?.landmarkDescription,
						pipelineChainage: item?.pipelineChainage,
						smartTLP : item.tlpId != 0 ? true : false,
					},
				);

			}
		}

		else if(item.rouMarker=='KMP' && props.changeKMP === true){
			KMP.push(				{
				geocode:[item.latitude,item.longitude],
				landmarkDescription: item?.landmarkDescription,
				pipelineChainage: item?.pipelineChainage,
				ODMeter: item?.ODMeter !== null && item?.ODMeter !== undefined ? item.ODMeter : null,
			},);
		}

		else if(item.rouMarker=='TP' && props.changeTP === true){
			Turn.push(				{
				geocode:[item.latitude,item.longitude],
				landmarkDescription: item?.landmarkDescription,
				pipelineChainage: item?.pipelineChainage
			},);
		}

	});

	wellinfo &&  wellinfo.map((item,index)=>{
		if(index==0){
			center.push({lat : item.latitude,lng : item.longitude});
		}
		if(props.changeWell === true){
			if(item && item.latitude && item && item.longitude){				
				wellMarkers.push(
					{
						location:[item.latitude,item.longitude], 
						name:item && item.name.toUpperCase(),
						id:item.id
					}
				);

			}
		}
		

	});
	
	if(props.changeEvent === true && props.eventData) {
		eventsicons && eventsicons.map((icondata)=>{
			props.eventData && props.eventData.map((item)=>{ 
				let severity = item.eventConfidence >= 0.9 &&item.eventConfidence <= 1 ? 3 : item.eventConfidence >= 0.8 && item.eventConfidence < 0.9 ? 2 : item.eventConfidence >= 0.5 && item.eventConfidence < 0.8 ? 1 : 0;
				if(item.eventType==icondata.type){
					event_markers.push([item.locationDetails.Lat,item.locationDetails.Long, icondata.severity[parseInt(severity)], item.eventType, item.id]);
				}
			});
		});
	} else {
		event_markers = [];
	}

	if(props.eventDetails) {
		center = [];
		eventsicons && eventsicons.map((icondata)=>{
			let severity = props.eventDetails.eventConfidence >= 0.9 && props.eventDetails.eventConfidence <= 1 ? 3 : props.eventDetails.eventConfidence >= 0.8 && props.eventDetails.eventConfidence < 0.9 ? 2 : props.eventDetails.eventConfidence >= 0.5 && props.eventDetails.eventConfidence < 0.8 ? 1 : 0;
			if(props.eventDetails.eventType==icondata.type){
				center.push({lat : props.eventDetails.latitude,lng : props.eventDetails.longitude});
				event_details_marker.push([props.eventDetails.latitude, props.eventDetails.longitude , icondata.severity[parseInt(severity)], props.eventDetails.eventType, props.eventDetails.id]);
			}
		});
	} else {
		event_details_marker = [];
	}

	if(props.changeAlarm === true && props.alarmData) {
		eventsicons && eventsicons.map((icondata)=>{
			props.alarmData && props.alarmData.map((item)=>{
				if(item.eventType==icondata.type){
					alarm_markers.push([item.locationDetails.Lat,item.locationDetails.Long, icondata.severity[item.alarmSeverity], item.eventType, item.newid]);
				}
			});
		});
	} else {
		alarm_markers = [];
	}

	if(props.alarmDetails) {
		center = [];
		eventsicons && eventsicons.map((icondata)=>{
			if(props.alarmDetails.eventType==icondata.type){
				center.push({lat : props.alarmDetails.locationDetails.Lat,lng : props.alarmDetails.locationDetails.Long});
				alarm_details_marker.push([props.alarmDetails.locationDetails.Lat, props.alarmDetails.locationDetails.Long, icondata.severity[props.alarmDetails.alarmSeverity], props.alarmDetails.eventType ]);
			}
		});
	}
	if(allPidsData.length>0 && props.pidsRegionData && props.pidsRegionData.length==0 || props.pidsRegionData==undefined ) {
		allPidsData && allPidsData.map((item)=>{
			allPidsDatas.push([parseFloat(item.latitude), parseFloat(item.longitude), item.value, item.label]);
		});
	} 
	else if(props.pidsRegionData.length > 0) {
		props.pidsRegionData && props.pidsRegionData.map((item)=>{
			center=[];
			allPidsDatas.push([parseFloat(item.latitude), parseFloat(item.longitude), item.value, item.label]);
			center.push({lat : parseFloat(item.latitude),lng : parseFloat(item.longitude)});
			zoom = 17;
		});
	} 
	else {
		allPidsDatas = [];
	}

	if(props.pigData) {
		props.pigData && props.pigData.map((item)=>{
			pig_data.push([parseFloat(item.Latitude), parseFloat(item.Longitude), item.id]);
		});
		
	} else {
		pig_data = [];
	}

	if(props.linewalkerData && oneviewType != 'well') {
		props.linewalkerData && props.linewalkerData.map((item)=>{
			if(props.center){
				center=[];
				center.push({lat : parseFloat(props.center.lat), lng : parseFloat(props.center.lng)});
			}
			linewalker_markers.push([parseFloat(item.currentLocation.latitude), parseFloat(item.currentLocation.longitude), item.userId, item.allocationId, item.user.cellPhone, item.user.firstName + ' ' + item.user.lastName, item.status, { userTypeId: item.user.usertypeId }]);
		});
		
	} else {
		linewalker_markers = [];
	}


	if(props.patch_data && oneviewType != 'well'){
		props.patch_data && props.patch_data.map((item)=>{
			if (props.changeinspectionPoint===true) {
				startAllocation.push([parseFloat(item.startLocation.latitude), parseFloat(item.startLocation.longitude)]);
				endAllocation.push([parseFloat(item.endLocation.latitude), parseFloat(item.endLocation.longitude)]);
			} else {
				startAllocation = [];
				endAllocation = [];
			}
			item.liveLocation.map((inneritem)=>{
				if (props.changeinspectionLine===true) {
					center=[];
					inspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					if(inneritem.direction=='opposite'){
						oppositeinspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					}
					else{
						sameinspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					}
					center.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					zoom = 19;
				} else {
					inspectionline = [];
					oppositeinspectionline = [];
					sameinspectionline = [];
				}
				if (props.changeinspectionPoint===true) {
					center=[];
					const datetime = inneritem?.currenttimestamp.replace(' ', 'T') + 'Z';
					const newdatetime = datetimeCovertertolocal(datetime);
					inspectionPoints.push([parseFloat(inneritem.latitude),parseFloat(inneritem.longitude),{'currenttimestamp' : `${newdatetime?.localdate} ${newdatetime?.localtime}`},{ 'usertypeId' : item?.usertypeId},{'velocity':inneritem?.velocity},
						{'nextPipeline': `${inneritem?.nearestdistance?.rouMarker} ${inneritem?.nearestdistance?.pipelineChainage} `},{'nearestdistance': Math.round(inneritem?.nearestdistance?.distance_km * 1000)}]);
					center.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					zoom = 19;
				} else {
					inspectionPoints = [];
				}
			});

		});
	}

	if(props.vulnerable_data && oneviewType != 'well') {
		props.vulnerable_data && props.vulnerable_data.map((item)=>{
			if (props.changeinspectionPoint===true) {
				startAllocation.push([parseFloat(item.startLocation.latitude), parseFloat(item.startLocation.longitude)]);
				endAllocation.push([parseFloat(item.endLocation.latitude), parseFloat(item.endLocation.longitude)]);
			} else {
				startAllocation = [];
				endAllocation = [];
			}
			item.liveLocation.map((inneritem)=>{
				if (props.changeinspectionLine===true) {
					center = [];
					inspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					if(inneritem.direction=='opposite'){
						oppositeinspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					}
					else{
						sameinspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					}
					center.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					zoom = 19;
				} else {
					inspectionline = [];
					oppositeinspectionline = [];
					sameinspectionline = [];
				}
				if (props.changeinspectionPoint===true) {
					center = [];
					const datetime = inneritem?.currenttimestamp.replace(' ', 'T') + 'Z';
					const newdatetime = datetimeCovertertolocal(datetime);
					inspectionPoints.push([parseFloat(inneritem.latitude),parseFloat(inneritem.longitude),{'currenttimestamp' : `${newdatetime?.localdate} ${newdatetime?.localtime}`},{ 'usertypeId' : item?.usertypeId},{'velocity':inneritem?.velocity},
						{'nextPipeline': `${inneritem?.nearestdistance?.rouMarker} ${inneritem?.nearestdistance?.pipelineChainage} `},{'nearestdistance': Math.round(inneritem?.nearestdistance?.distance_km * 1000)}]);
					center.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					zoom = 19;
				} else {
					inspectionPoints = [];
				}
			});

		});
	}

	if(props.inspectionData && oneviewType != 'well') {
		props.inspectionData && props.inspectionData.map((item)=>{
			if (props.changeinspectionPoint===true) {
				startAllocation.push([parseFloat(item.startLocation.latitude), parseFloat(item.startLocation.longitude)]);
				endAllocation.push([parseFloat(item.endLocation.latitude), parseFloat(item.endLocation.longitude)]);
			} else {
				startAllocation = [];
				endAllocation = [];
			}
			item.liveLocation.map((inneritem)=>{
				if (props.changeinspectionLine===true || props.changePlayinspectionLine ===true) {
					center = [];
					inspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					if(inneritem.direction=='opposite'){
						oppositeinspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					}
					else{
						sameinspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					}
					inspectionline.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					if(props.changePlayinspectionLine ===true){

						center.push({lat : parseFloat(playinspectionLine && playinspectionLine.length > 0 && playinspectionLine.at(-1).lat),lng : parseFloat(playinspectionLine && playinspectionLine.length && playinspectionLine.at(-1).lng)});
					}else{
						center.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					}
					zoom = 19;
				} else {
					inspectionline = [];
					oppositeinspectionline = [];
					sameinspectionline = [];

				}
				if (props.changeinspectionPoint===true) {
					const datetime = inneritem?.currenttimestamp.replace(' ', 'T') + 'Z';
					const newdatetime = datetimeCovertertolocal(datetime);
					inspectionPoints.push([parseFloat(inneritem.latitude),parseFloat(inneritem.longitude),{'currenttimestamp' : `${newdatetime?.localdate} ${newdatetime?.localtime}`},{ 'usertypeId' : item?.usertypeId},{'velocity':inneritem?.velocity},
						{'nextPipeline': `${inneritem?.nearestdistance?.rouMarker} ${inneritem?.nearestdistance?.pipelineChainage} `},{'nearestdistance': Math.round(inneritem?.nearestdistance?.distance_km * 1000)}]);
					center = [];
					center.push({lat : parseFloat(inneritem.latitude),lng : parseFloat(inneritem.longitude)});
					zoom = 19;
				} else {
					inspectionPoints = [];
				}
			});

		});
	}
	
	if(props.TLPDetails && oneviewType != 'well') {
		center=[];
		tlp_details_marker.push([props.TLPDetails.latitude,props.TLPDetails.longitude]);
		center.push({lat : parseFloat(props.TLPDetails.latitude),lng : parseFloat(props.TLPDetails.longitude)});
		zoom = 17;
	} else {
		tlp_details_marker = [];
	}

	if(props.pigPids && props.pigPids.length>0 && oneviewType != 'well'){
		props.pigPids.map((item)=>{
			pigPidsData.push(item);
		});
	}

	const handleOnclick = (value, type) => {
		if (type === 'event' || type === 'alarm') {
			onClick(value[4]);
		} else if(type === 'pig') {
			onClick(value[0], value[1], value[2]);
		} else if (type === 'lineWalker') {
			onClick(value[2], value[3], value[4], value[5], value[6]);
		}
		else if(type=='well'){
			
			onClick(value.id,'well',value.name);
		}
		setCenter({lat : value[0],lng : value[1]});
	};

	const path =  inspectionPoints && inspectionPoints.map(p => ({
		lat: p[0],
		lng: p[1]
	}));


	const detectionTrajectories = [];


	if (seadataprops && Array.isArray(seadataprops)) {

		seadataprops.forEach((detection) => {

			// Only show processed trajectories
			if (!detection?.isTrajectoryProcessed) {
				return;
			}

			// Use corrected data if trajectory is corrected
			const result = detection?.isTrajectoryCorrected
				? detection?.correctedDetectionResults
				: detection?.detectionResults;

			if (!result) {
				return;
			}

			const latitudes = result?.sourceLatitude || [];
			const longitudes = result?.sourceLongitude || [];

			const sourceDistances = result?.sourceDistances || [];
			const sourceAngles = result?.sourceAngle || [];
			const sbr = result?.sbr || [];

			const timeStart = result?.timeStart || [];
			const timeEnd = result?.timeEnd || [];

			if (!latitudes.length || !longitudes.length) {
				return;
			}

			// Find the point closest to CPA distance
			let cpaIndex = -1;

			if (result?.cpaSourceDistance != null) {

				let smallestDifference = Infinity;

				sourceDistances.forEach((distance, index) => {

					const difference = Math.abs(
						Number(distance) -Number(result.cpaSourceDistance)
					);

					if (difference < smallestDifference) {
						smallestDifference = difference;
						cpaIndex = index;
					}
				});
			}

			const points = latitudes
				.map((latitude, index) => {

					const longitude = longitudes[index];

					if (
						latitude == null ||longitude == null ||!Number.isFinite(Number(latitude)) ||!Number.isFinite(Number(longitude))
					) {
						return null;
					}

					return {
						lat: Number(latitude),
						lng: Number(longitude),

						pointIndex: index,

						sourceDistance:sourceDistances[index],

						sourceAngle:sourceAngles[index],

						sbr:sbr[index],

						timeStart:timeStart[index],

						timeEnd:timeEnd[index],

						isCPA: index === cpaIndex
					};
				})
				.filter(Boolean);

			if (points.length > 0) {

				// peakCoordinates carries the "Fibre Detection" point for this
				// trajectory — mirrors the same field RealtimeDetection.jsx's
				// memoized `detectionTrajectories` puts on each trajectory for
				// SeaMap. Accepts either a real {latitude, longitude} object or
				// the 'No Data' sentinel string used upstream.
				const peakCoordinates =
					detection.peakCoordinates &&
					detection.peakCoordinates !== 'No Data'
						? detection.peakCoordinates
						: null;

				detectionTrajectories.push({

					peakDistance : detection.peakDistance,

					id: detection.id,

					detectionAlgorithmTypeId:detection.detectionalgorithmtypeId,

					detectionAlgorithmTypeName:detection.detectionAlgorithmTypeName,

					multipleModelName:detection.multipleModelName,

					channelName:detection.channelName,

					// Same field SeaMap.jsx's DetectionHoverContent shows as
					// "Pids Name" — carried through so the Google Maps hover
					// title matches SeaMap's exactly, point-for-point.
					pidsName:detection.pidsName,

					eventType:detection.eventType,

					isTrajectoryCorrected:detection.isTrajectoryCorrected,

					cpaSourceDistance:result.cpaSourceDistance,

					cpaSourceDatetime:result.cpaSourceDatetime,

					sourceSpeedms:result.sourceSpeedms,

					sourceSpeedKts:result.sourceSpeedKts,

					peakCoordinates,

					points
				});
			}
		});
	}

	// ============================================================
	// CENTER ON THE DETECTED / CPA POINT
	// ============================================================
	// ============================================================
	// CENTER ON THE DETECTED / CPA POINT
	// ============================================================
	if (seadataprops && detectionTrajectories.length > 0) {
		center = [];

		const propCenter = resolveCenterProp(props.center);

		if (propCenter) {
			center.push(propCenter);
		} else {
			const firstTrajectory = detectionTrajectories[0];
			const cpaPoint =
				firstTrajectory.points.find((point) => point.isCPA) ||
				firstTrajectory.points[firstTrajectory.points.length - 1];

			if (cpaPoint) {
				center.push({ lat:  cpaPoint && cpaPoint.lat, lng:  cpaPoint && cpaPoint.lng });
			}
		}

		zoom = 17;
	} else if (allPidsDatas.length > 0) {
		// No detection algorithm output (either no detections were passed
		// in at all, or every one of them failed the
		// `isTrajectoryProcessed` / valid-lat-lng checks above, so
		// detectionTrajectories ended up empty) — fall back to centering
		// on the first plotted PIDS marker instead of leaving the map on
		// whatever center it had before (or FALLBACK_CENTER).
		center = [];
		center.push({ lat: allPidsDatas[0][0], lng: allPidsDatas[0][1] });
		zoom = 15;
	}


	const algorithmColors = [
		'#FF0000',
		'#0000FF',
		'#00AA00',
		'#FF9900',
		'#9900FF',
		'#00AAAA',
		'#FF00AA',
		'#795548',
		'#607D8B',
		'#8BC34A'
	];

	const algorithmColorMap = {};

	detectionTrajectories.forEach((trajectory) => {
		const algorithmId = trajectory.detectionAlgorithmTypeId;

		if (algorithmColorMap[algorithmId]) {
			return;
		}

		const colorIndex =
        Object.keys(algorithmColorMap).length %
        algorithmColors.length;

		algorithmColorMap[algorithmId] = algorithmColors[colorIndex];
	});

	const trajectoryPath = (trajectory) => {
		return trajectory.points.map((point) => ({
			lat: point.lat,
			lng: point.lng
		}));
	};

	// ============================================================
	// PER-POINT DETECTION HOVER TITLE (ported from SeaMap.jsx's
	// DetectionHoverContent, using pickValueForIndex above)
	//
	// Builds the SAME multi-line hover text for a single point of a
	// single trajectory, used for EVERY detection marker on this map —
	// the starting-point ship icon AND every other plotted point — so
	// they all show identical fields: Event Type, Pids Name, Algorithm
	// (superadmin only, same gating as SeaMap), Channel, Latitude,
	// Longitude, Distance to fibre, Speed, and Start Time. Speed and
	// distance are pulled out for THIS point's index specifically
	// (pickValueForIndex / point.sourceDistance) instead of printing a
	// trajectory-level field that may actually be a per-point array —
	// that mismatch was what produced the duplicated "3.05,3.05 m/s"
	// value on the starting point and left every other point blank.
	// ============================================================
	const buildDetectionPointTitle = (trajectory, point, pointIndex) => {
		const speedValue = pickValueForIndex(trajectory.sourceSpeedKts, pointIndex);
		const distanceValue = point?.sourceDistance ?? pickValueForIndex(trajectory.cpaSourceDistance, pointIndex);
		const pointTime = point?.timeStart ? datetimeCovertertolocal(point.timeStart) : null;

		let title =
			`${langdata && langdata.EventType ? langdata.EventType : 'Event Type'}: ${trajectory.eventType || 'No Data'}\n` +
			`${langdata && langdata.PidsName ? langdata.PidsName : 'Pids Name'}: ${trajectory.pidsName || 'No Data'}\n`;

		if (['superadmin'].includes(permission)) {
			title += `${langdata && langdata.Algorithm ? langdata.Algorithm : 'Algorithm'}: ${trajectory.detectionAlgorithmTypeName || 'No Data'}\n`;
		}

		title +=
			`${langdata && langdata.Channel ? langdata.Channel : 'Channel'}: ${trajectory.channelName || 'No Data'}\n` +
			`${langdata && langdata.Latitude ? langdata.Latitude : 'Latitude'}: ${point?.lat}\n` +
			`${langdata && langdata.Longitude ? langdata.Longitude : 'Longitude'}: ${point?.lng}\n` +
			`${langdata && langdata.DistanceToFibre ? langdata.DistanceToFibre + '(m)' : 'Distance to fibre (m)'}: ${distanceValue ?? 'No Data'}`;
			
		if (pointTime) {
			title += `\n${langdata && langdata.StartTime ? langdata.StartTime : 'Start Time'}: ${pointTime.localdate} ${pointTime.localtime}`;
		}
		title +=	`\n${langdata && langdata.Speed ? langdata.Speed : 'Speed'}: ${Number.isFinite(Number(speedValue)) ? Number(speedValue).toFixed(3) + ' m/s' : 'No Data'}`;

		return title;
	};

	// ============================================================
	// MAP VIEW STABILITY — center/zoom no longer "snap back"
	//
	// Previously `center`/`zoom` above were recomputed as brand-new
	// object/array references on EVERY render (including renders caused
	// purely by toggling a checkbox in Map.jsx, e.g. POINTS / POLY LINES /
	// FIBRE DETECTION / any layer checkbox). Passing a fresh `center` object
	// straight into <GoogleMap center={...}> on every render made the map
	// re-apply that center/zoom each time, which snapped the view back and
	// undid any manual pan/zoom the user had just done — exactly the "moves
	// back to where I zoomed in" / "should stay as it is when I click any
	// options" bug.
	//
	// Fix: keep the actual live view (`viewCenter`/`viewZoom`) in state, and
	// only update that state from an effect keyed on a `focusSignature` that
	// intentionally excludes every checkbox/toggle prop — it only reacts to
	// genuinely new data (region/chainage change, a specific event/alarm/TLP
	// being opened, new pids-region data, a new batch of detections, a
	// marker click via handleOnclick/stateCenter, or a linewalker recenter).
	// Toggling any layer checkbox re-renders GMap and recomputes the local
	// `center`/`zoom` variables above as before (still needed to know WHERE
	// to go when a real focus change happens), but does NOT itself change
	// `focusSignature`, so the mounted map's view is left completely alone.
	// ============================================================
	const [mapInstance, setMapInstance] = useState(null);
	const handleMapLoad = (map) => setMapInstance(map);
	const handleMapUnmount = () => setMapInstance(null);

	const [viewCenter, setViewCenter] = useState(null);
	const [viewZoom, setViewZoom] = useState(zoom);
	// const initialViewSetRef = useRef(false);

	// desiredCenter is guaranteed to always be a valid {lat, lng} object —
	// FALLBACK_CENTER (a stable module-level constant, see above) is used
	// whenever no real pipeline/detection/etc. data has produced a center
	// yet. This is what stops <GoogleMap defaultCenter={null}> from ever
	// happening, which is what left the map rendering as a blank box.
	const desiredCenter = (center && center[0]) ? center[0] : FALLBACK_CENTER;

	const focusSignature = JSON.stringify([
		props.regionId,
		props.startCh,
		props.endCh,
		props.eventDetails ? props.eventDetails.id : null,
		props.alarmDetails ? props.alarmDetails.id : null,
		props.TLPDetails ? `${props.TLPDetails.latitude},${props.TLPDetails.longitude}` : null,
		props.pidsRegionData && props.pidsRegionData.length,
		props.pidsRegionData && props.pidsRegionData[0]
			? `${props.pidsRegionData[0].latitude},${props.pidsRegionData[0].longitude}`
			: null,
		props.center ? JSON.stringify(props.center) : null,
		detectionIdsKey,
		stateCenter ? `${stateCenter.lat},${stateCenter.lng}` : null,
	]);

	useEffect(() => {
		if (
			!mapInstance ||
        !desiredCenter ||
        !Number.isFinite(desiredCenter.lat) ||
        !Number.isFinite(desiredCenter.lng)
		) {
			return;
		}

		mapInstance.panTo(desiredCenter);
		mapInstance.setZoom(zoom);

		setViewCenter(desiredCenter);
		setViewZoom(zoom);
	}, [focusSignature, mapInstance]);

	// Live-follow the walking/patrol-line PLAY replay (car marker) WITHOUT
	// touching viewCenter/viewZoom — this pans the already-mounted map
	// imperatively so it doesn't fight the user's own zoom/pan, and doesn't
	// re-trigger the "snap back to default zoom" behavior described above.
	useEffect(() => {
		if (
			props.changePlayinspectionLine &&
			!hasDetection &&
			mapInstance &&
			playinspectionLine &&
			playinspectionLine.length > 0
		) {
			const last = playinspectionLine.at(-1);
			if (Number.isFinite(last.lat) && Number.isFinite(last.lng)) {
				mapInstance.panTo({ lat: last.lat, lng: last.lng });
			}
		}
	}, [playinspectionLine, props.changePlayinspectionLine, mapInstance]);

	
	return (
		<div style={{ width: '100%', height: props.height ? props.height : '100vh' }}>
			{!isLoaded ? (
				<h1></h1>
			) : (
				<GoogleMap
					mapContainerClassName="map-container"
					defaultCenter={viewCenter || desiredCenter}
					defaultZoom={viewZoom}
					onLoad={handleMapLoad}
					onUnmount={handleMapUnmount}
					options={{
						// Lets the user zoom with a plain mouse-wheel scroll —
						// no Ctrl/Cmd key or on-screen zoom buttons required.
						gestureHandling: 'greedy',
						scrollwheel: true,
					}}
				>
					{showAISVessels && <AisVesselsLayer map={mapInstance} enabled={props.showAISVessels === true} />}
					
					{tempMapArray && Object.values(tempMapArray).map((item) => {
						let pipeline = [];
						let odmeter = [];
						let pipelineBorder = [];
						if(props.changePipe === true && oneviewType != 'well') {
							item.map((data) => {
								pipeline.push({lat : data.latitude,lng : data.longitude});
							});
						} else {
							pipeline = [];
						}
						if(props.changeFiber === true && oneviewType != 'well') {
							item.map((data) => {
								odmeter.push({lat : data.fibreLatitude, lng : data.fibreLongitude});
							});
						} else {
							odmeter = [];
						}
						if(props.changeBorder === true && oneviewType != 'well') {
							item.map((data) => {
								pipelineBorder.push({lat : data.latitude,lng : data.longitude});
							});
						} else {
							pipelineBorder = [];
						}
						return (
							<>
								<PolylineF path={pipeline} options={{ strokeOpacity: 0.8, strokeColor: '#000000', strokeWeight: 5}}/>
								<PolylineF path={odmeter} options={{ strokeOpacity: 0.8, strokeColor: '#036CD6', strokeWeight: 5}}/>
								<PolylineF path={pipelineBorder} options={{ strokeOpacity: 0.32, strokeColor: '#fc8383', strokeWeight: 25 }}/>
							</>
						);

					})}

					{teampOptionalMapArray && Object.values(teampOptionalMapArray).map((item) => {
						let optionalpipeline = [];
						if(props.changeOptionalPipe===true) {
							item.map((data) => {
								optionalpipeline.push({lat : data.walkingLatitude,lng : data.walkingLongitude});
							});
						} else {
							optionalpipeline = [];
						}
						return (
							<>
								<PolylineF path={optionalpipeline} options={{ strokeOpacity: 0.8, strokeColor: 'orange', strokeWeight: 5}}/>
							</>
						);
					})}

					<PolylineF path={props.changePlayinspectionLine ? playinspectionLine : sameinspectionline} options={{ strokeOpacity: 0.8, strokeColor: 'green', strokeWeight: 5}}/>

					<PolylineF path={props.changePlayinspectionLine ? [] : oppositeinspectionline} options={{ strokeOpacity: 0.8, strokeColor: 'violet', strokeWeight: 5}}/>

					{/* ============================================================
					    STATIC detection trajectories — the full line and per-point
					    markers for every detection. Always mounted (including while
					    PLAY is running) so the base trajectory never disappears.
					    Gated by the "POINTS" / "POLY LINES" checkboxes next to PLAY
					    in Map.jsx (showDetectionPoints defaults true, showDetection
					    Polyline defaults false). The static ship icon at the fixed
					    CPA point is only shown while PLAY is OFF
					    (`!props.changePlayinspectionLine`) — while PLAY is on, the
					    moving ship rendered further below (in the PLAY ANIMATION
					    block) takes its place and travels from the trajectory's
					    start to its end. When PLAY is switched off, this static ship
					    reappears at its original position. The CPA ship icon and
					    trajectory line's color-coding are unaffected by the Points /
					    Poly Lines toggles.

					    Every marker's hover title below now goes through
					    buildDetectionPointTitle(trajectory, point, pointIndex) so
					    every point — not just the start point — shows the full set
					    of fields with values correct for THAT point.
					============================================================ */}
					<>
						{showDetectionPolyline && detectionTrajectories.map((trajectory, index) => (
							<PolylineF
								key={`detection-trajectory-${trajectory.id}-${index}`}
								path={trajectoryPath(trajectory)}
								options={{
									strokeOpacity: 0.9,
									strokeColor: algorithmColorMap[trajectory.detectionAlgorithmTypeId],
									strokeWeight: 5,
									zIndex: 1
								}}
							/>
						))}

						{!props.changePlayinspectionLine && detectionTrajectories.map((trajectory, trajectoryIndex) => {
							const startPoint = trajectory.points[0];

							if (!startPoint) {
								return null;
							}

							return (
								<MarkerF
									key={`start-ship-${trajectory.id}-${trajectoryIndex}`}
									position={{
										lat: startPoint.lat,
										lng: startPoint.lng
									}}
									icon={{
										url: vesselcpa,
										scaledSize: new window.google.maps.Size(28, 45)
									}}
									title={buildDetectionPointTitle(trajectory, startPoint, 0)}
									zIndex={2}
								/>
							);
						})}

						{showDetectionPoints && detectionTrajectories.flatMap((trajectory) =>
							trajectory.points.map((point, pointIndex) => {
								return (
									<MarkerF
										key={`detection-point-${trajectory.id}-${pointIndex}`}
										position={{
											lat: point.lat,
											lng: point.lng
										}}
										icon={{
											path: window.google.maps.SymbolPath.CIRCLE,
											scale: 5,
											fillColor: 'red',
											fillOpacity: 1,
											strokeColor: 'white',
											strokeWeight: 1
										}}
										title={buildDetectionPointTitle(trajectory, point, pointIndex)}
									/>
								);
							})
						)}

						{/* ============================================================
						    FIBRE DETECTION — plots each trajectory's peakCoordinates
						    point, exactly like SeaMap.jsx's peakCoordinateIcon marker.
						    Gated by the "FIBRE DETECTION" checkbox (defaults ON).
						============================================================ */}
						{showFibreDetection && detectionTrajectories.map((trajectory, trajectoryIndex) => {
							const peakLat = trajectory.peakCoordinates ? parseFloat(trajectory.peakCoordinates.latitude) : NaN;
							const peakLng = trajectory.peakCoordinates ? parseFloat(trajectory.peakCoordinates.longitude) : NaN;
							const peakDistance = trajectory.peakDistance ? Number(trajectory.peakDistance).toFixed(2) : 'No Data';

							if (!Number.isFinite(peakLat) || !Number.isFinite(peakLng)) {
								return null;
							}

							return (
								<MarkerF
									key={`fibre-detection-${trajectory.id}-${trajectoryIndex}`}
									position={{ lat: peakLat, lng: peakLng }}
									icon={{
										url: peakCoordinateIconUrl,
										scaledSize: new window.google.maps.Size(18, 18),
										anchor: new window.google.maps.Point(9, 9)
									}}
									title={
										`${langdata && langdata.FibreDetection ? langdata.FibreDetection : 'Fibre Detection'}\n` +
										`${langdata && langdata.FibreDistance ? langdata.FibreDistance + ' (m)' : 'Fibre Distance (m)'}: ${trajectory.peakDistance ? peakDistance : 'No Data'}\n` 
										// `${langdata && langdata.Longitude ? langdata.Longitude : 'Longitude'}: ${trajectory.peakCoordinates.longitude}`
									}
									zIndex={3}
								/>
							);
						})}

						{/* ============================================================
						    SHIP-FIBRE LINK — a dashed line from every plotted ship
						    point on a trajectory to that trajectory's peakCoordinates
						    ("Fibre Detection") point, exactly like SeaMap.jsx's
						    showShipFibreLink block. Gated by the "SHIP-FIBRE LINK"
						    checkbox (defaults OFF).
						============================================================ */}
						{showShipFibreLink && detectionTrajectories.flatMap((trajectory) => {
							const peakLat = trajectory.peakCoordinates ? parseFloat(trajectory.peakCoordinates.latitude) : NaN;
							const peakLng = trajectory.peakCoordinates ? parseFloat(trajectory.peakCoordinates.longitude) : NaN;

							if (!Number.isFinite(peakLat) || !Number.isFinite(peakLng)) {
								return [];
							}

							return trajectory.points
								.filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
								.map((point, pointIndex) => (
									<PolylineF
										key={`ship-fibre-link-${trajectory.id}-${pointIndex}`}
										path={[
											{ lat: point.lat, lng: point.lng },
											{ lat: peakLat, lng: peakLng }
										]}
										options={{
											strokeColor: algorithmColorMap[trajectory.detectionAlgorithmTypeId] || '#795548',
											strokeOpacity: 0,
											strokeWeight: 3.5,
											icons: dashedLineIcons,
											zIndex: 1
										}}
									/>
								));
						})}
					</>

					{/* ============================================================
					    ANIMATED (PLAY) detection trajectories — a growing green
					    line + a single moving vessel (ship icon) marker PER
					    detection, sliced from the exact same path array the
					    static red line above uses. Once PLAY is switched off,
					    `playedCount` resets to 0 (see the effect above), this
					    block stops rendering, and the static ship icon block
					    above reappears at the CPA point.
					============================================================ */}
					{props.changePlayinspectionLine && detectionTrajectories.map((trajectory) => {
						const fullPath = trajectoryPath(trajectory);
						const animatedPath = fullPath.slice(0, playedCount);

						if (animatedPath.length === 0) {
							return null;
						}

						const headPoint = animatedPath[animatedPath.length - 1];

						return (
							<React.Fragment key={`play-detection-${trajectory.id}`}>
								<PolylineF
									path={animatedPath}
									options={{ strokeOpacity: 0.9, strokeColor: 'green', strokeWeight: 4, zIndex: 999 }}
								/>

								<MarkerF
									position={headPoint}
									icon={{ url: vesselcpa, scaledSize: new window.google.maps.Size(28, 45) }}
									title={trajectory.channelName || ''}
									zIndex={999}
								/>
							</React.Fragment>
						);
					})}

					{markers.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item.geocode[0], lng : item.geocode[1] }} icon = {CHicon} title={`${langdata && langdata.Landmark ? langdata.Landmark : 'Landmark'}: ${item.landmarkDescription}, ${langdata && langdata.Chainage ? langdata.Chainage + ' (m)' : 'Pipeline Chainage (m)'}: ${item.pipelineChainage}, ${(langdata && langdata.FiberLength) ? langdata.FiberLength  : 'Fiber Length (m)'}: ${item.ODMeter}`}/>
					))}

					{WB.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item.geocode[0], lng : item.geocode[1] }} icon = {WBicon}  title={`${langdata && langdata.Landmark ? langdata.Landmark : 'Landmark'}: ${item.landmarkDescription}, ${langdata && langdata.pipelineChainage ? langdata.pipelineChainage : 'Pipeline Chainage'}: ${item.pipelineChainage}`}/>
					))}

					{TLP.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item.geocode[0], lng : item.geocode[1] }} icon = {item.pipelineChainage && item.smartTLP ? smartTLPicon : TLPicon} title={`${langdata && langdata.Landmark ? langdata.Landmark : 'Landmark'}: ${item.landmarkDescription}, ${langdata && langdata.pipelineChainage ? langdata.pipelineChainage : 'Pipeline Chainage'}: ${item.pipelineChainage}`}/>
					))}

					{KMP.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item.geocode[0], lng : item.geocode[1]}} icon = {KMPicon} title={`${langdata && langdata.Landmark ? langdata.Landmark : 'Landmark'}: ${item.landmarkDescription}, ${langdata && langdata.Chainage ? langdata.Chainage + ' (m)' : 'Pipeline Chainage (m)'}: ${item.pipelineChainage}, ${(langdata && langdata.FiberLength) ? langdata.FiberLength  : 'Fiber Length (m)'}: ${item.ODMeter}`}/>
					))}

					{Turn.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item.geocode[0], lng : item.geocode[1] }} icon = {TPicon} title={`${langdata && langdata.Landmark ? langdata.Landmark : 'Landmark'}: ${item.landmarkDescription}, ${langdata && langdata.pipelineChainage ? langdata.pipelineChainage : 'Pipeline Chainage'} : ${item.pipelineChainage}`}/>
					))}

					{event_markers.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }} icon = {item[2]} title = {item[3]} onClick={() => {handleOnclick(item,'event');}} />
					))}

					{event_details_marker.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }}  icon = {item[2]} title = {item[3]}/>
					))}

					{alarm_markers.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }} icon = {item[2]} title = {item[3]} onClick={() => {handleOnclick(item,'alarm');}} />
					))}

					{alarm_details_marker.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }} icon = {item[2]} title = {item[3]} />
					))}

					{allPidsDatas.map((item, index) => (
						<>
							<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }}  icon = {pids} title = {item[3]} />
							{pigPidsData.map((inneritem)=>{
								if(inneritem==item[2]){
									return(
										<CircleF
											key={index}
											center={{
												lat: parseFloat(item[0]),
												lng: parseFloat(item[1])
											}}
											radius={300}
											options={{
												strokeColor: 'red'
											}}
										/>
									);
								}
							})}
						</>
					))}

					{pig_data.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }} icon = {defaultimg} onClick={() => {handleOnclick(item, 'pig');}} />
					))}

					{linewalker_markers.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }} 
							icon = {{url :item[7]?.userTypeId === 13 || item[7]?.userTypeId === 7 ? QRTIcon : LWIcon,
								scaledSize: new window.google.maps.Size(20, 25) 
							}} 
							onClick={() => {handleOnclick(item, 'lineWalker');}} />
					))}

					{startAllocation.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }} icon = {StartIcon} title='START' />
					))}


					{endAllocation.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }} icon = {EndIcon} title='END' />
					))}


					{inspectionPoints.map((item, index) => (
						<MarkerF key = {index} position={{ lat : item[0], lng : item[1] }} 
							icon = {{url :item[3]?.usertypeId === 13 || item[3]?.usertypeId === 7 ? QRTmarker : LWmarker,
								scaledSize: new window.google.maps.Size(25, 25) 
							}}  
							title={
								`${langdata?.AvgVelocity ?? 'Avg Velocity (km/h)'}: ${item[4]?.velocity}\n` +
								`${langdata?.DateTime ?? 'Current timestamp'}: ${item[2]?.currenttimestamp}\n` +
								`${langdata?.info ?? 'Info'}: ${item[5]?.nextPipeline} -> ${item[6]?.nearestdistance}${langdata?.meters ?? 'm'} ${langdata?.away ?? 'away'}`
							} />
					))}

					{
						inspectionPoints ? <PolylineF path={path} options={{strokeOpacity: 0.8,strokeColor: 'green',strokeWeight: 3,icons: [{icon: {path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,scale: 3,strokeColor: '#FF0000',strokeOpacity: 0.7},offset: '0',repeat: '150px',},],}}/> : null
					}
					{
						/*
						 * Moving marker for the legacy walking/patrol-line PLAY replay only.
						 * Every detection trajectory now renders its own moving marker above
						 * (one per detection, alongside its own growing green line), so this
						 * fallback only fires when there's no detection data loaded at all.
						 */
						!hasDetection && props.changePlayinspectionLine && playinspectionLine && playinspectionLine.length > 0 ?
							<MarkerF
								position={{ lat : playinspectionLine.at(-1).lat, lng : playinspectionLine.at(-1).lng }}
								icon={car}
							/> : 
							null
					}					
					{tlp_details_marker.map((item, index) => (
						<MarkerF key = {index} position={{ lat : Number(item[0]), lng : Number(item[1]) }} icon = {smartTLPicon}/>
					))}

					{wellMarkers && wellMarkers.map((item, index) => (						
						<MarkerF key = {index} position={{ lat : item && item.location[0], lng : item && item.location[1] }} title={item.name} onClick={() => {handleOnclick(item, 'well');}}
							icon={{
								url: wellicon, 
								scaledSize: new window.google.maps.Size(40, 40) 
							}}
						/>
					))}

					{/* ============================================================
					    REFERENCE COORDINATES — plotted interrogator/coordinate
					    points, exactly like SeaMap.jsx's CoordinateMarker. Clicking a
					    marker opens an InfoWindow with the same details SeaMap shows
					    in its hover Tooltip/Popup, plus a Delete action (only if
					    `onDeleteCoordinate` was supplied) that asks for inline
					    confirmation before calling it — mirrors SeaMap's
					    CoordinateDeleteActions. Newly added coordinates appear here
					    automatically as soon as `coordinatesData` includes them (the
					    parent re-fetches/updates that list after a successful add).
					    Gated by the "REFERENCE COORDINATES" checkbox (defaults ON).
					============================================================ */}
					{showReferenceCoordinate && coordinatesData.map((coordinate) => {
						const latitude = parseFloat(coordinate.latitude);
						const longitude = parseFloat(coordinate.longitude);


						if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
							return null;
						}

						return (
							<MarkerF
								key={`coordinate-${coordinate.id}`}
								position={{ lat: latitude, lng: longitude }}
								icon={{
									url: coordinatePinIconUrl,
									scaledSize: new window.google.maps.Size(30, 42),
									anchor: new window.google.maps.Point(15, 40)
								}}
								title={coordinate.name || (langdata && langdata.Coordinate ? langdata.Coordinate : 'Coordinate')}
								onClick={() => {
									setActiveCoordinateId(coordinate.id);
									setConfirmDeleteCoordinateId(null);
								}}
								zIndex={5}
							>
								{activeCoordinateId === coordinate.id && (
									<InfoWindowF
										position={{ lat: latitude, lng: longitude }}
										onCloseClick={() => {
											setActiveCoordinateId(null);
											setConfirmDeleteCoordinateId(null);
										}}
									>
										<div style={{ minWidth: 220 }}>
											<strong>
												{coordinate.name || (langdata && langdata.Coordinate ? langdata.Coordinate : 'Coordinate')}
											</strong>
											<br />
											{(langdata && langdata.channelName ? langdata.channelName : 'Channel Name')}: {coordinate.channelName || 'No Data'}
											<br />
											{(langdata && langdata.PidsName ? langdata.PidsName : 'Pids Name')}: {coordinate.pidsName || 'No Data'}
											<br />
											{(langdata && langdata.Latitude ? langdata.Latitude : 'Latitude')}: {latitude}
											<br />
											{(langdata && langdata.Longitude ? langdata.Longitude : 'Longitude')}: {longitude}
											<br />
											{(langdata && langdata.Distance ? langdata.Distance + ' (km)' : 'Distance (km)')}: {coordinate.distance || 'No Data'}
											<br />
											{(langdata && langdata.FibreDistance ? langdata.FibreDistance + ' (m)' : 'Fibre Distance (m)')}: {coordinate.ODMeter !== null ? coordinate.ODMeter : 'No Data'}
											<br />
											{(langdata && langdata.DateTime ? langdata.DateTime : 'Date Time')}: {coordinate.createdAt || 'No Data'}

											{onDeleteCoordinate ? (
												<div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
													{confirmDeleteCoordinateId === coordinate.id ? (
														<>
															<div style={{ fontSize: 12, marginBottom: 6, color: '#333' }}>
																{(langdata && langdata.ConfirmDeleteCoordinate) || 'Delete this coordinate?'}
															</div>
															<button
																type="button"
																disabled={deletingCoordinateId === coordinate.id}
																style={{ border: '1px solid #d32f2f', background: '#d32f2f', color: '#fff', borderRadius: 3, padding: '3px 10px', fontSize: 12, cursor: 'pointer', marginRight: 6 }}
																onClick={async () => {
																	setDeletingCoordinateId(coordinate.id);
																	try {
																		await onDeleteCoordinate(coordinate);
																		setActiveCoordinateId(null);
																	} finally {
																		setDeletingCoordinateId(null);
																		setConfirmDeleteCoordinateId(null);
																	}
																}}
															>
																{deletingCoordinateId === coordinate.id
																	? ((langdata && langdata.Deleting) || 'Deleting...')
																	: ((langdata && langdata.YesDelete) || 'Yes, delete')}
															</button>
															<button
																type="button"
																style={{ border: '1px solid #9aa4af', background: '#fff', color: '#333', borderRadius: 3, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}
																onClick={() => setConfirmDeleteCoordinateId(null)}
															>
																{(langdata && langdata.Cancel) || 'Cancel'}
															</button>
														</>
													) : (
														<button
															type="button"
															style={{ border: '1px solid #d32f2f', color: '#d32f2f', background: '#fff', borderRadius: 3, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}
															onClick={() => setConfirmDeleteCoordinateId(coordinate.id)}
														>
															{(langdata && langdata.Delete) || 'Delete'}
														</button>
													)}
												</div>
											) : null}
										</div>
									</InfoWindowF>
								)}
							</MarkerF>
						);
					})}
					{/* FIBRE COORDINATE MARKER */}
					{showReferenceCoordinate && coordinatesData.map((coordinate) => {
						const fibreLatitude = parseFloat(coordinate.fibrelatitude);
						const fibreLongitude = parseFloat(coordinate.fibrelongitude);

						if (
							!Number.isFinite(fibreLatitude) ||
        !Number.isFinite(fibreLongitude)
						) {
							return null;
						}

						return (
							<MarkerF
								key={`fibre-coordinate-${coordinate.id}`}
								position={{
									lat: fibreLatitude,
									lng: fibreLongitude
								}}
								icon={{
									url: peakCoordinateIconUrl,
									scaledSize: new window.google.maps.Size(18, 18),
									anchor: new window.google.maps.Point(9, 9)
								}}
								title={
									coordinate.name ||
                (langdata && langdata.Coordinate ? langdata.Coordinate : 'Coordinate')
								}
								zIndex={4}
							/>
						);
					})}

					{/* ============================================================
					    COORDINATE-FIBRE LINK — a dashed magenta line from a plotted
					    reference coordinate to its associated fibre point, exactly
					    like SeaMap.jsx's showCoordinateFibreLine block. Gated by the
					    "COORDINATE-FIBRE LINK" checkbox (defaults OFF).
					============================================================ */}
					{showCoordinateFibreLine && coordinatesData.map((coordinate) => {
						const latitude = parseFloat(coordinate.latitude);
						const longitude = parseFloat(coordinate.longitude);
						const fibreLatitude = parseFloat(coordinate.fibrelatitude);
						const fibreLongitude = parseFloat(coordinate.fibrelongitude);

						const hasCoord = Number.isFinite(latitude) && Number.isFinite(longitude);
						const hasFibre = Number.isFinite(fibreLatitude) && Number.isFinite(fibreLongitude);

						if (!hasCoord || !hasFibre) {
							return null;
						}

						return (
							<PolylineF
								key={`coordinate-fibre-line-${coordinate.id}`}
								path={[
									{ lat: latitude, lng: longitude },
									{ lat: fibreLatitude, lng: fibreLongitude }
								]}
								options={{
									strokeColor: '#ff00ff',
									strokeOpacity: 0,
									strokeWeight: 2,
									icons: dashedLineIcons,
									zIndex: 2
								}}
							/>
						);
					})}

					{/* <DrawingManager /> */}
				</GoogleMap>
			)}
		</div>
	);
};

export default GMap;